"use client";

import React from "react";
import { FileText, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HeroBanner() {
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll();
  const [dragOffset, setDragOffset] = React.useState({
    x: 0,
    y: 0
  });
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStart = React.useRef({
    x: 0,
    y: 0
  });
  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setDragOffset({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y
    });
  };
  const handlePointerUp = () => setIsDragging(false);
  return <section data-section-id="22995" id="inicio" className="relative min-h-[90vh] w-full overflow-hidden bg-background text-foreground flex items-center justify-center py-20">
      {}
      <style jsx global>{`
        @keyframes grain {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-5%, -10%); }
          20% { transform: translate(-15%, 5%); }
          30% { transform: translate(7%, -25%); }
          40% { transform: translate(-5%, 25%); }
          50% { transform: translate(-15%, 10%); }
          60% { transform: translate(15%, 0%); }
          70% { transform: translate(0%, 15%); }
          80% { transform: translate(3%, 35%); }
          90% { transform: translate(-10%, 10%); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(30px, -45px) rotate(4deg); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-15px, 15px) rotate(-3deg); }
        }
        @keyframes idle-rotate {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(3deg); }
        }
        @keyframes fade-up-soft {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .paper-grain::before {
          content: "";
          position: absolute;
          top: -100%;
          left: -100%;
          width: 300%;
          height: 300%;
          background-image: url("https://wpvc-images.s3.us-east-1.amazonaws.com/images/1596556/img/stardust.png");
          opacity: 0.15;
          pointer-events: none;
          animation: grain 8s steps(10) infinite;
        }
        .text-shadow-tactile {
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
      `}</style>

      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 paper-grain bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1596556/img/mexican_woman_abroad_documents.png)] bg-no-repeat bg-cover bg-center" aria-label="Mexicana en el extranjero gestiona apostilla documentos y trámites mexicanos para servicios internacionales" role="img">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1596556/img/mexican_woman_abroad_documents.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1596556/img/mexican_woman_abroad_documents.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1596556/img/mexican_woman_abroad_documents.png-wvc-sizes" alt="Mujer mexicana en el extranjero gestiona apostilla de documentos mexicanos" className="sr-only" loading="lazy" decoding="async" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/60 to-background" />
      </div>

      

      {}
      <div ref={contentRef} className={`relative z-20 max-w-5xl mx-auto px-6 text-center transition-all duration-1000 transform ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <h2 className="text-secondary font-sans font-bold tracking-wider uppercase mb-4 text-sm md:text-base">orgullosamente mexicanos – trámites de apostilla</h2>

        <h1 className="font-sans font-bold text-5xl md:text-7xl lg:text-8xl tracking-tighter text-foreground mb-4 leading-[1.1]">Apostilla de documentos y trámites mexicanos para servicios internacionales</h1>
        <p className="text-sm font-medium text-muted-foreground/60 mb-8 uppercase tracking-widest">Hecho por mexicanos, para mexicanos en el extranjero.</p>

        <p className="font-sans font-normal text-lg md:text-xl text-muted-foreground tracking-tight max-w-3xl mx-auto mb-12 leading-relaxed">Si estás lejos de México y necesitas gestionar el trámite de apostilla para tu acta de nacimiento, antecedentes no penales, título o poder notarial, te ayudamos con atención clara, seguimiento real y envío internacional para que presentes tus documentos mexicanos en España, Estados Unidos u otro país sin complicaciones.</p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6">
          <Link to="https://wa.me/message/GPXE5OUSFQOBF1" newTab>
            <Button size="lg" variant="secondary" className="w-full sm:w-auto font-bold rounded-md px-10 py-7 text-lg shadow-lg transition-all hover:scale-105 active:scale-95 transition-transform active:shadow-lg">Cotiza por WhatsApp</Button>
          </Link>

          <Link to="#preguntas-frecuentes">
            <Button variant="outline" size="lg" className="w-full sm:w-auto bg-background/50 border-primary/20 text-primary hover:bg-primary/5 hover:text-primary rounded-md px-10 py-7 text-lg backdrop-blur-sm transition-all hover:scale-105">
              ¿Qué es una apostilla? 
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [{
          "@type": "Question",
          "name": "¿Qué documentos mexicanos se pueden apostillar?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Se pueden apostillar actas de nacimiento, matrimonio, defunción, antecedentes no penales, títulos profesionales y poderes notariales."
          }
        }, {
          "@type": "Question",
          "name": "¿Cuánto tiempo tarda el trámite de apostilla?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El tiempo varía según el documento y la entidad emisora, pero generalmente gestionamos el proceso de manera eficiente con envío internacional."
          }
        }, {
          "@type": "Question",
          "name": "¿Cómo apostillar actas de nacimiento?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Para apostillar un acta de nacimiento mexicana desde el extranjero, nosotros gestionamos la obtención del documento actualizado y realizamos el trámite ante la autoridad correspondiente en México antes de enviarlo a tu domicilio internacional."
          }
        }, {
          "@type": "Question",
          "name": "¿Cómo apostillar antecedentes no penales?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El trámite de apostilla de antecedentes no penales federales requiere la gestión directa en la Ciudad de México. Nosotros nos encargamos de todo el proceso legal y logístico para que recibas tu documento listo para ser usado en el país donde te encuentres."
          }
        }]
      })
    }} />
    </section>;
}